import type { EstimateAssumption, EstimateExclusion, EstimateWarning, EstimatorInput } from "../../types/index.js";
import { getScopeMultiplier } from "../scope-multipliers.js";
import {
  applyItem,
  findItem,
  scaleAppliedItem,
  type AppliedLineItem,
  type ProjectCalculationOutput,
  type ProjectCalculatorFn
} from "./shared.js";

/**
 * The Basement Suite Estimator (Phase 9).
 *
 * This is deliberately NOT the basement calculator (rules/basement.ts)
 * reused unchanged - a basement suite is a self-contained secondary
 * dwelling unit, not a finished basement with a different label, and the
 * brief for this phase is explicit that reusing the basement estimator
 * unchanged is not acceptable. Concretely, this calculator differs from
 * calculateBasement in what it treats as in-scope by default (a suite's
 * kitchen and bathroom are asked as required, narrower questions - see
 * packages/config/src/questions/basement-suite.ts - never "none"), and it
 * prices/discloses several categories a plain finished basement never
 * needs to think about: a separate entrance, fire separation from the
 * rest of the house, electrical service capacity (sub-panel/upgrade and
 * optional separate metering), plumbing/drain capacity, and sound
 * separation between units.
 *
 * Base finishing, the envelope (framing/drywall/insulation, driven by
 * current condition), ceiling, and flooring reuse exactly the same
 * pricing categories and logic as calculateBasement, via the same shared
 * catalog item ids and shared question ids (see
 * packages/config/src/questions/basement.ts's SHARED_BASEMENT_QUESTIONS) -
 * per the brief, this phase uses the existing pricing and calculation
 * framework rather than inventing a parallel one.
 *
 * Compliance stance (per the brief): nothing in this calculator ever
 * implies that a computed estimate, or selecting the "legal secondary
 * suite" intent, means the result will actually satisfy any
 * municipality's zoning, building code, or legal-suite requirements.
 * Every category whose real-world requirement depends on jurisdiction or
 * site conditions (egress, fire separation, electrical capacity,
 * plumbing capacity, sound separation, the entrance, and permits) carries
 * an explicit "this must be verified" warning rather than a silent
 * assumption either way - see the "standing disclosures" section below.
 */

const CONDITION_ENVELOPE_MULTIPLIERS: Record<string, number> = {
  unfinished: 1.0,
  partially_finished: 0.6,
  previously_finished_renovating: 0.25
};

const BATHROOM_ITEM_BY_SCOPE: Record<string, string> = {
  existing_renovation: "basement_bathroom_renovation",
  new_bathroom: "basement_bathroom_addition"
};

const KITCHEN_ITEM_BY_SCOPE: Record<string, string> = {
  kitchenette: "basement_kitchenette",
  full_kitchen: "basement_full_kitchen"
};

const CEILING_ITEM_BY_TYPE: Record<string, string> = {
  drywall: "basement_ceiling",
  drop_ceiling: "basement_ceiling_drop",
  exposed_finished: "basement_ceiling_exposed"
};

const FLOORING_ITEM_BY_TIER: Record<string, string> = {
  basic: "basement_flooring_basic",
  mid_range: "basement_flooring",
  premium: "basement_flooring_premium"
};

const HVAC_ITEM_BY_SCOPE: Record<string, string | null> = {
  not_needed: null,
  extend_existing: "basement_hvac_extend",
  dedicated_zone: "basement_hvac"
};

const BEDROOM_COUNT_BY_ANSWER: Record<string, number> = {
  "0": 0,
  "1": 1,
  "2": 2,
  "3_plus": 3
};

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

function bedroomCount(input: EstimatorInput): number {
  const answer = rawString(input, "basement_suite_bedroom_count");
  return answer ? (BEDROOM_COUNT_BY_ANSWER[answer] ?? 0) : 0;
}

export const calculateBasementSuite: ProjectCalculatorFn = (input, catalog, regionalMultiplier) => {
  const applied: AppliedLineItem[] = [];
  const assumptions: EstimateAssumption[] = [];
  const exclusions: EstimateExclusion[] = [];
  const warnings: EstimateWarning[] = [];
  let anchorMissing = false;

  const finishLevel = input.finishLevel;
  const sqft = input.roomSize?.value;
  const condition = rawString(input, "basement_current_condition") ?? "unfinished";
  const intent = rawString(input, "basement_suite_intent");
  const bedrooms = bedroomCount(input);

  // --- Base finishing (anchor) + scope adjustment, envelope, ceiling, flooring ---
  // Identical methodology to calculateBasement - see rules/basement.ts.
  let baseTypicalForPercentage = 0;
  if (sqft && sqft > 0) {
    const base = applyItem(catalog, "basement_finishing_base", { quantity: sqft, finishLevel, regionalMultiplier });
    if (base) {
      const scopeMultiplier = getScopeMultiplier(input.scope);
      const scoped = scaleAppliedItem(base, scopeMultiplier);
      applied.push(scoped);
      baseTypicalForPercentage = scoped.result.typical?.amount ?? 0;
      assumptions.push({
        code: "scope_multiplier_applied",
        message: `Base finishing cost adjusted by a ${scopeMultiplier}x scope multiplier for "${input.scope}".`
      });
    }

    const envelopeMultiplier = CONDITION_ENVELOPE_MULTIPLIERS[condition] ?? 1.0;
    for (const category of ["basement_framing_drywall", "basement_insulation"]) {
      const line = applyItem(catalog, category, { quantity: sqft, finishLevel, regionalMultiplier });
      if (line) applied.push(envelopeMultiplier === 1.0 ? line : scaleAppliedItem(line, envelopeMultiplier));
    }
    if (envelopeMultiplier < 1.0) {
      assumptions.push({
        code: "envelope_scope_reduced_for_condition",
        message:
          condition === "previously_finished_renovating"
            ? "Framing, drywall, and insulation costs were reduced since the space was previously finished and much of the envelope likely already exists - confirm what's being reused vs. rebuilt once walls are opened up."
            : "Framing, drywall, and insulation costs were reduced for a partially-finished basement, since some of this work already exists."
      });
    }

    const flooringTier = rawString(input, "basement_flooring_type");
    const flooringItemId = flooringTier ? FLOORING_ITEM_BY_TIER[flooringTier] : "basement_flooring";
    if (flooringItemId) {
      const flooring = applyItem(catalog, flooringItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (flooring) applied.push(flooring);
      if (!flooringTier) {
        assumptions.push({ code: "flooring_defaulted", message: 'No flooring selection was provided - assumed a mid-range flooring replacement.' });
      }
    }

    const ceilingType = rawString(input, "basement_ceiling_type");
    const ceilingItemId = ceilingType ? CEILING_ITEM_BY_TYPE[ceilingType] : "basement_ceiling";
    if (ceilingItemId) {
      const ceiling = applyItem(catalog, ceilingItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (ceiling) applied.push(ceiling);
      if (!ceilingType) {
        assumptions.push({ code: "ceiling_type_defaulted", message: 'No ceiling type was provided - assumed a standard drywall ceiling.' });
      }
    }
  } else {
    anchorMissing = true;
  }

  // --- Bathroom (required for a self-contained suite, unlike a plain basement) ---
  const bathroomScope = rawString(input, "basement_suite_bathroom_scope");
  const bathroomItemId = bathroomScope ? BATHROOM_ITEM_BY_SCOPE[bathroomScope] : undefined;
  if (bathroomItemId) {
    const bathroom = applyItem(catalog, bathroomItemId, { finishLevel, regionalMultiplier });
    if (bathroom) applied.push(bathroom);
  } else {
    // Defensive fallback only - the question is required, so this should be rare.
    const bathroom = applyItem(catalog, "basement_bathroom_addition", { finishLevel, regionalMultiplier });
    if (bathroom) applied.push(bathroom);
    assumptions.push({
      code: "suite_bathroom_scope_defaulted",
      message: 'No bathroom scope was provided for the suite - assumed a brand-new full bathroom, since a self-contained suite requires one.'
    });
  }

  // --- Kitchen (required for a self-contained suite, unlike a plain basement) ---
  const kitchenScope = rawString(input, "basement_suite_kitchen_scope");
  const kitchenItemId = kitchenScope ? KITCHEN_ITEM_BY_SCOPE[kitchenScope] : undefined;
  if (kitchenItemId) {
    const kitchen = applyItem(catalog, kitchenItemId, { finishLevel, regionalMultiplier });
    if (kitchen) applied.push(kitchen);
    if (kitchenScope === "kitchenette" && intent === "legal_secondary_suite") {
      warnings.push({
        code: "suite_kitchenette_may_not_qualify",
        message:
          "A kitchenette was selected for a suite intended as a legal secondary suite. Several Canadian municipalities require a \"full kitchen\" (not a kitchenette) for a space to qualify as a legal second dwelling unit - verify your municipality's specific definition before finalizing scope.",
        severity: "caution"
      });
    }
  } else {
    const kitchen = applyItem(catalog, "basement_full_kitchen", { finishLevel, regionalMultiplier });
    if (kitchen) applied.push(kitchen);
    assumptions.push({
      code: "suite_kitchen_scope_defaulted",
      message: 'No kitchen scope was provided for the suite - assumed a full kitchen, since a self-contained suite requires one.'
    });
  }

  // --- Bedroom(s) ------------------------------------------------------------
  if (bedrooms > 0) {
    const bedroom = applyItem(catalog, "basement_bedroom", { quantity: bedrooms, finishLevel, regionalMultiplier });
    if (bedroom) applied.push(bedroom);
  } else {
    exclusions.push({ code: "excludes_basement_suite_bedroom", message: "No bedroom was selected for the suite - excluded from this estimate." });
  }

  // --- Other spaces (from suite room composition) -----------------------------
  if (Array.isArray(input.rawAnswers["basement_suite_other_spaces"]) && (input.rawAnswers["basement_suite_other_spaces"] as unknown[]).includes("living_area")) {
    assumptions.push({
      code: "suite_living_area_included_in_base",
      message: "A living/sitting area was indicated - its cost is covered by the base finishing square footage above, not priced as a separate line item."
    });
  }

  // --- Egress (tied to bedroom count, always addressed - never silently skipped) ---
  const egressStatus = rawString(input, "basement_suite_egress_status");
  if (egressStatus === "new_or_modified_required") {
    const egress = applyItem(catalog, "basement_egress_window", { quantity: Math.max(bedrooms, 1), regionalMultiplier });
    if (egress) applied.push(egress);
    warnings.push({
      code: "basement_suite_egress_requires_verification",
      message:
        "A new or modified egress window was indicated. Egress requirements for a secondary suite depend on window-well dimensions, sill height, and local code, and typically apply per bedroom - this estimate includes a placeholder allowance, but the actual requirement must be verified on-site and with your municipality before finalizing budget.",
      severity: "caution"
    });
  } else if (egressStatus === "unknown" || (!egressStatus && bedrooms > 0)) {
    warnings.push({
      code: "basement_suite_egress_unconfirmed",
      message:
        "Egress for the suite's bedroom(s) was not confirmed. Nearly every Canadian jurisdiction requires a code-compliant egress window per bedroom in a secondary suite - this was not priced and must be verified with your municipality before finalizing budget.",
      severity: "caution"
    });
    exclusions.push({ code: "excludes_basement_suite_egress_work", message: "Egress work is not included pending verification - see the egress warning above." });
  } else if (egressStatus === "not_applicable_no_bedroom" && bedrooms > 0) {
    warnings.push({
      code: "basement_suite_egress_contradiction",
      message: '"No bedroom in the suite" was selected for egress, but at least one bedroom was selected elsewhere - please re-check these two answers, since egress requirements follow the bedroom count.',
      severity: "caution"
    });
  } else {
    exclusions.push({ code: "excludes_basement_suite_egress_work", message: "No new or modified egress work was requested for the suite - excluded from this estimate." });
  }

  // --- Separate entrance -------------------------------------------------------
  const entranceStatus = rawString(input, "basement_suite_separate_entrance");
  if (entranceStatus === "new_entrance_needed") {
    const entrance = applyItem(catalog, "basement_suite_secondary_entrance", { regionalMultiplier });
    if (entrance) applied.push(entrance);
  } else if (entranceStatus === "modify_existing") {
    const entrance = applyItem(catalog, "basement_suite_secondary_entrance", { regionalMultiplier });
    if (entrance) {
      applied.push(scaleAppliedItem(entrance, 0.4));
      assumptions.push({
        code: "suite_entrance_modification_scaled",
        message: "A separate-entrance cost was included at a reduced scope for modifying an existing door/entrance rather than creating a brand-new one."
      });
    }
  } else if (entranceStatus === "shared_entrance_only") {
    warnings.push({
      code: "basement_suite_shared_entrance_risk",
      message:
        "Only a shared entrance through the main house is planned. Most Canadian jurisdictions require an independent entrance for a legal secondary suite - this plan is unlikely to meet that requirement and should be confirmed with your local zoning/building department before proceeding.",
      severity: "caution"
    });
    exclusions.push({ code: "excludes_basement_suite_entrance_work", message: "No separate-entrance work was requested - excluded from this estimate." });
  } else if (entranceStatus === "already_exists") {
    exclusions.push({ code: "excludes_basement_suite_entrance_work", message: "A separate exterior entrance already exists - no entrance work included in this estimate." });
  } else {
    warnings.push({
      code: "basement_suite_entrance_unconfirmed",
      message: "The separate-entrance situation was not confirmed. An independent entrance is a common legal-suite requirement - verify this with your municipality before finalizing budget.",
      severity: "caution"
    });
  }

  // --- Fire separation -----------------------------------------------------------
  const fireSeparation = rawString(input, "basement_suite_fire_separation");
  if ((fireSeparation === "full_assembly_needed" || fireSeparation === "likely_partial_upgrade") && sqft && sqft > 0) {
    const fireItem = applyItem(catalog, "basement_suite_fire_separation", { quantity: sqft, regionalMultiplier });
    if (fireItem) {
      applied.push(fireSeparation === "full_assembly_needed" ? fireItem : scaleAppliedItem(fireItem, 0.4));
    }
  } else {
    exclusions.push({
      code: "excludes_basement_suite_fire_separation_work",
      message: "No fire-separation upgrade work was priced - see the fire-separation verification notice below."
    });
  }
  warnings.push({
    code: "basement_suite_fire_separation_requires_verification",
    message:
      "Fire separation and interconnected smoke/CO alarm requirements between the suite and the rest of the house must be verified by a building/fire inspection - this cannot be confirmed from a questionnaire, regardless of what was selected above.",
    severity: "caution"
  });

  // --- Electrical capacity (distinct from general electrical changes below) ------
  const electricalCapacity = rawString(input, "basement_suite_electrical_capacity");
  if (electricalCapacity === "subpanel_or_upgrade_needed" || electricalCapacity === "separate_metering_desired") {
    const upgrade = applyItem(catalog, "basement_suite_electrical_service_upgrade", { regionalMultiplier });
    if (upgrade) applied.push(upgrade);
    if (electricalCapacity === "separate_metering_desired") {
      const metering = applyItem(catalog, "basement_suite_separate_metering", { regionalMultiplier });
      if (metering) applied.push(metering);
      assumptions.push({
        code: "suite_separate_metering_implies_service_upgrade",
        message: "Separate metering was requested along with a service upgrade - installing a separate meter typically requires panel work to be done first."
      });
    }
  } else if (electricalCapacity === "existing_sufficient") {
    exclusions.push({ code: "excludes_basement_suite_electrical_upgrade", message: "Existing electrical service was believed sufficient - no service upgrade included in this estimate." });
  } else {
    warnings.push({
      code: "basement_suite_electrical_capacity_unconfirmed",
      message: "Electrical service capacity for the suite was not confirmed. A second kitchen and independent circuits often require a sub-panel or service upgrade - this was not priced and should be verified by a licensed electrician.",
      severity: "caution"
    });
  }

  // --- Plumbing/drain capacity (distinct from general plumbing changes below) ---
  const plumbingCapacity = rawString(input, "basement_suite_plumbing_capacity");
  if (plumbingCapacity === "upgrade_likely_needed") {
    const upgrade = applyItem(catalog, "basement_suite_plumbing_capacity_upgrade", { regionalMultiplier });
    if (upgrade) applied.push(upgrade);
  } else if (plumbingCapacity === "existing_sufficient") {
    exclusions.push({ code: "excludes_basement_suite_plumbing_upgrade", message: "Existing plumbing/drain capacity was believed sufficient - no capacity upgrade included in this estimate." });
  } else {
    warnings.push({
      code: "basement_suite_plumbing_capacity_unconfirmed",
      message: "Plumbing/drain capacity for the suite's second kitchen and bathroom was not confirmed. This was not priced and should be verified by a licensed plumber, especially in an older home.",
      severity: "caution"
    });
  }

  // --- Mechanical: general electrical / plumbing changes, HVAC ------------------
  if (input.workType.electricalChanges === "yes") {
    const electrical = applyItem(catalog, "basement_electrical", { finishLevel, regionalMultiplier });
    if (electrical) applied.push(electrical);
  } else {
    exclusions.push({ code: "excludes_basement_electrical", message: "Excludes general electrical changes - none were requested for the basement suite (beyond any service-capacity upgrade priced above)." });
  }

  if (input.workType.plumbingChanges === "yes") {
    const plumbing = applyItem(catalog, "basement_plumbing", { finishLevel, regionalMultiplier });
    if (plumbing) applied.push(plumbing);
  } else {
    exclusions.push({ code: "excludes_basement_plumbing", message: "Excludes general plumbing changes - none were requested for the basement suite (beyond any bathroom/kitchen/capacity work priced above)." });
  }

  const hvacScope = rawString(input, "basement_hvac_scope");
  const hvacItemId = hvacScope ? HVAC_ITEM_BY_SCOPE[hvacScope] : null;
  if (hvacItemId) {
    const hvac = applyItem(catalog, hvacItemId, { finishLevel, regionalMultiplier });
    if (hvac) applied.push(hvac);
  } else if (!hvacScope || hvacScope === "not_needed") {
    exclusions.push({ code: "excludes_basement_hvac", message: "No basement HVAC work was requested - excluded from this estimate." });
    if (hvacScope === "not_needed") {
      assumptions.push({
        code: "suite_hvac_independence_not_confirmed",
        message: "\"Not needed\" was selected for heating/cooling. Some jurisdictions expect a rental secondary suite to have independent temperature control - confirm this is acceptable for your intended use before finalizing scope."
      });
    }
  }

  // --- Sound separation between the suite and the rest of the house -------------
  const soundSeparation = rawString(input, "basement_suite_sound_separation");
  if (soundSeparation === "enhanced" && sqft && sqft > 0) {
    const sound = applyItem(catalog, "basement_suite_sound_separation", { quantity: sqft, regionalMultiplier });
    if (sound) applied.push(sound);
  } else {
    exclusions.push({ code: "excludes_basement_suite_sound_separation", message: "No enhanced sound-separation work was priced - see the sound-separation verification notice below." });
    warnings.push({
      code: "basement_suite_sound_separation_requires_verification",
      message: "Standard construction may not meet the minimum sound-transmission rating some jurisdictions require between dwelling units - verify your local requirement before finalizing scope.",
      severity: "caution"
    });
  }

  // --- Additional features -------------------------------------------------
  if (input.rawAnswers["basement_fireplace"] === true) {
    const fireplace = applyItem(catalog, "basement_fireplace", { finishLevel, regionalMultiplier });
    if (fireplace) applied.push(fireplace);
  } else {
    exclusions.push({ code: "excludes_basement_fireplace", message: "A fireplace was not requested - excluded from this estimate." });
  }

  if (input.rawAnswers["basement_built_ins"] === true) {
    const builtIns = applyItem(catalog, "basement_built_ins", { finishLevel, regionalMultiplier });
    if (builtIns) applied.push(builtIns);
  } else {
    exclusions.push({ code: "excludes_basement_built_ins", message: "Built-in cabinetry/shelving was not requested - excluded from this estimate." });
  }

  // --- Demolition ------------------------------------------------------------
  if (input.workType.demolitionRequired === "yes" && findItem(catalog, "basement_demolition")) {
    const demolition = applyItem(catalog, "basement_demolition", { regionalMultiplier, baseAmountForPercentage: baseTypicalForPercentage });
    if (demolition) applied.push(demolition);
  }

  // --- Accessibility --------------------------------------------------------
  if (input.accessibility.required === "yes") {
    const accessibility = applyItem(catalog, "basement_accessibility", { finishLevel, regionalMultiplier });
    if (accessibility) applied.push(accessibility);
  }

  // --- Permit / design allowance (suite-specific: legalization/inspection) -------
  if (input.designAndPermits.needsDesignServices === "yes" || input.designAndPermits.permitLikelyRequired !== "no") {
    const permitDesign = applyItem(catalog, "basement_suite_legal_permit_design", { finishLevel, regionalMultiplier });
    if (permitDesign) applied.push(permitDesign);
  } else {
    warnings.push({
      code: "basement_suite_permit_assumption_unusual",
      message:
        "A permit was marked as not required for this basement suite. Adding a secondary suite - legal or not - almost always requires a building permit in Canadian municipalities; double-check this with your local building department before proceeding without one.",
      severity: "caution"
    });
  }

  // --- Standing disclosures ----------------------------------------------------
  // Per the brief: never imply that a computed number, or picking "legal
  // secondary suite," means the result satisfies local requirements -
  // and always identify jurisdiction/site-dependent categories as
  // needing verification rather than silently assuming either way.
  warnings.push({
    code: "basement_moisture_foundation_unknown",
    message:
      "Moisture and foundation conditions behind existing finishes cannot be assessed from a questionnaire. This estimate assumes no hidden water infiltration, mold, or foundation repair is needed - a site inspection may materially change pricing if issues are found once walls are opened.",
    severity: "caution"
  });

  warnings.push({
    code: "basement_suite_compliance_not_guaranteed",
    message:
      intent === "legal_secondary_suite"
        ? "Selecting \"legal secondary suite\" describes your intent - it does not mean this estimate, or the scope priced within it, will satisfy your municipality's zoning, building code, or legal-suite requirements. Whether this space can legally become a second dwelling unit depends on your specific property, zoning bylaw, and a code/inspection review that this questionnaire cannot perform."
        : "This estimate is for a basement suite, not a determination of whether the space can legally become a second dwelling unit. That depends on your specific property, local zoning bylaw, and a code/inspection review this questionnaire cannot perform.",
    severity: intent === "legal_secondary_suite" ? "blocking" : "caution"
  });

  warnings.push({
    code: "basement_suite_jurisdiction_verification_required",
    message:
      "Several categories in this estimate depend on your specific jurisdiction and site conditions and must be verified locally before finalizing budget or proceeding with construction: egress, fire separation, electrical service capacity, plumbing/drain capacity, sound separation, the separate entrance, and permit requirements.",
    severity: "caution"
  });

  if (condition === "previously_finished_renovating") {
    warnings.push({
      code: "basement_previously_finished_inspection_notice",
      message:
        "Because this basement was previously finished, actual scope (what's salvageable vs. what needs to be redone) can only be confirmed once existing finishes are opened up - a site inspection may materially change this estimate.",
      severity: "caution"
    });
  }
  if (input.workType.structuralChanges === "yes" || input.workType.structuralChanges === "unknown") {
    assumptions.push({
      code: "basement_structural_engineering_flag",
      message:
        "Structural changes were indicated (e.g. support posts/beams, load-bearing walls). This estimate does not include engineering costs - a structural engineer's assessment and likely a structural permit are required before final pricing."
    });
  }

  const output: ProjectCalculationOutput = { applied, assumptions, exclusions, anchorMissing, warnings };
  return output;
};
